import React, { useState, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';
import calendarService from '../services/calendarService';
import CalendarGrid from '../components/calendar/CalendarGrid';
import CalendarList from '../components/calendar/CalendarList';
import CalendarLegend from '../components/calendar/CalendarLegend';
import DayDetailModal from '../components/calendar/DayDetailModal';
import { fetchOccasions, fetchCenterCalendar, getMonthOccasionMap } from '../services/islamicOccasionsService';
import FloatingActionButton from '../components/calendar/FloatingActionButton';
import SponsorshipFlow from '../components/sponsorship/SponsorshipFlow';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import useMediaQuery from '../hooks/useMediaQuery';
import '../styles/calendar.css';

/**
 * CalendarPage Component
 * Main calendar view with monthly grid and list views
 * Filters events by center_id from config
 */
const CalendarPage = () => {
  const { config, theme } = useConfig();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // State management
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [eventCount, setEventCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [sponsoringEvent, setSponsoringEvent] = useState(null);
  const [hasNextMonthData, setHasNextMonthData] = useState(false);
  const [hasPreviousMonthData, setHasPreviousMonthData] = useState(false);
  const [occasionMap, setOccasionMap] = useState(null);

  // Get center ID from config
  const centerId = config?.center_id || config?.id;
  const centerName = config?.name || 'Community Center';

  // Fetch events when component mounts or month changes
  useEffect(() => {
    if (centerId) {
      fetchEvents();
      fetchEventCount();
      checkAdjacentMonthsData();
    } else {
      setLoading(false);
      setError('No center configuration found');
    }
  }, [centerId, currentMonth, currentYear]);

  // Fetch Islamic occasions when month changes (if feature enabled)
  useEffect(() => {
    if (!config?.islamic_occasions_enabled) return;
    const loadOccasions = async () => {
      try {
        const [occasions, calendar] = await Promise.all([
          fetchOccasions(config.islamic_occasions_sheet_id),
          fetchCenterCalendar(config.google_sheet_id, centerId),
        ]);
        if (occasions.length > 0 && calendar.length > 0) {
          const map = getMonthOccasionMap(currentYear, currentMonth, calendar, occasions);
          setOccasionMap(map);
        }
      } catch (err) {
        console.warn('[CalendarPage] Failed to load Islamic occasions:', err.message);
      }
    };
    loadOccasions();
  }, [config?.islamic_occasions_enabled, config?.islamic_occasions_sheet_id, config?.google_sheet_id, centerId, currentMonth, currentYear]);
  
  // Check if adjacent months have data
  const checkAdjacentMonthsData = async () => {
    try {
      // Check next month
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
      const nextCount = await calendarService.getMonthEventCount(
        centerId,
        nextYear,
        nextMonth
      );
      setHasNextMonthData(nextCount > 0);
      
      // Check previous month
      const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
      const prevCount = await calendarService.getMonthEventCount(
        centerId,
        prevYear,
        prevMonth
      );
      setHasPreviousMonthData(prevCount > 0);
    } catch (err) {
      console.error('Error checking adjacent months:', err);
    }
  };

  // Removed auto-switch - always start with list view

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await calendarService.getMonthEvents(
        centerId,
        currentYear,
        currentMonth
      );
      
      console.log('[CalendarPage] Fetched events:', {
        centerId,
        year: currentYear,
        month: currentMonth,
        eventCount: data ? data.length : 0
      });
      
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventCount = async () => {
    try {
      const count = await calendarService.getMonthEventCount(
        centerId,
        currentYear,
        currentMonth
      );
      setEventCount(count);
    } catch (err) {
      console.error('Error fetching event count:', err);
    }
  };

  // Navigation handlers
  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth() + 1);
    setCurrentYear(today.getFullYear());
  };

  // View handlers - removed since we're showing both views

  const handleDateClick = (date, dayEvents) => {
    setSelectedDate({ date, events: dayEvents });
    setShowDayModal(true);
  };

  const handleEventSponsor = (event) => {
    setSponsoringEvent(event);
  };

  const handleMonthlySupport = () => {
    // Create a special "monthly" event object for sponsorship
    const monthlyEvent = {
      id: `monthly-${currentYear}-${currentMonth}`,
      title: `${getMonthName(currentMonth)} ${currentYear} Programs`,
      event_type: 'monthly_support',
      start_date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`,
      isMonthlySupport: true
    };
    setSponsoringEvent(monthlyEvent);
  };

  // Helper functions
  const getMonthName = (month) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1];
  };

  // Layout component
  const Layout = isMobile ? MobileLayout : DesktopLayout;

  // Styles
  const styles = {
    container: {
      backgroundColor: theme?.colors?.background || '#f8f9fa',
      minHeight: 'calc(100vh - 150px)',
      padding: isMobile ? '16px' : '32px 24px',
      fontFamily: theme?.typography?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      maxWidth: '1400px',
      margin: '0 auto',
      marginBottom: '24px'
    },
    navigationBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      padding: '16px 24px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    monthTitle: {
      fontSize: isMobile ? '20px' : '26px',
      fontWeight: '300',
      margin: 0
    },
    navButtons: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    navButton: {
      background: 'white',
      border: `2px solid ${theme?.colors?.primary || '#0f172a'}`,
      color: theme?.colors?.primary || '#0f172a',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '18px',
      fontWeight: '600',
      transition: 'all 0.2s',
      outline: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0
    },
    supportButton: {
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '25px',
      fontSize: '15px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    spiritualMessage: {
      fontStyle: 'italic',
      color: theme?.colors?.textSecondary || '#475569',
      fontSize: '14px',
      marginBottom: '20px',
      textAlign: 'center',
      padding: '0 8px',
      lineHeight: '1.4'
    },
    viewToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '20px',
      justifyContent: 'center'
    },
    toggleButton: {
      padding: '8px 20px',
      border: `1px solid ${theme?.colors?.primary || '#0f172a'}`,
      backgroundColor: 'transparent',
      color: theme?.colors?.primary || '#0f172a',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s',
      outline: 'none'
    },
    toggleButtonActive: {
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white'
    },
    calendarContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '16px'
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: `4px solid ${theme?.colors?.lightGray || '#e0e0e0'}`,
      borderTopColor: theme?.colors?.primary || '#0f172a',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    error: {
      maxWidth: '600px',
      margin: '0 auto',
      padding: '24px',
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '8px',
      color: '#856404',
      textAlign: 'center'
    },
    quote: {
      fontStyle: 'italic',
      color: theme?.colors?.textSecondary || '#475569',
      fontSize: '13px',
      textAlign: 'center',
      padding: '20px',
      borderTop: '1px solid #e2e8f0',
      marginTop: '40px',
      maxWidth: '800px',
      margin: '40px auto 0'
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loading}>
            <div style={styles.loadingSpinner}></div>
            <div style={{ color: theme?.colors?.primary }}>Loading calendar...</div>
          </div>
        </div>
      </Layout>
    );
  }

  // Render error state
  if (error) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.error}>
            <div>⚠️ {error}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          {/* Header with Navigation Arrows */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '12px' : '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '16px' : '24px',
            }}>
              <button
                onClick={handlePreviousMonth}
                style={{
                  ...styles.navButton,
                  opacity: 1,
                }}
                aria-label="Previous month"
              >
                ‹
              </button>
              <h1 style={{
                fontSize: isMobile ? '28px' : '40px',
                fontWeight: '600',
                margin: 0,
                color: theme?.colors?.primary || '#0f172a',
                minWidth: isMobile ? '180px' : '280px',
              }}>
                {getMonthName(currentMonth)} {currentYear}
              </h1>
              <button
                onClick={handleNextMonth}
                style={{
                  ...styles.navButton,
                  opacity: 1,
                }}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            {/* Hijri month subtitle — shows which Islamic months span this Gregorian month */}
            {occasionMap && occasionMap.size > 0 && (() => {
              // Get first and last day's Hijri month from the occasion map
              const firstKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
              const lastDay = new Date(currentYear, currentMonth, 0).getDate();
              const lastKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
              const firstData = occasionMap.get(firstKey);
              const lastData = occasionMap.get(lastKey);
              if (!firstData?.hijri && !lastData?.hijri) return null;
              const first = firstData?.hijri;
              const last = lastData?.hijri;
              let hijriLabel = '';
              if (first && last) {
                if (first.monthName === last.monthName && first.year === last.year) {
                  hijriLabel = `${first.monthName} ${first.year}`;
                } else if (first.year === last.year) {
                  hijriLabel = `${first.monthName} — ${last.monthName} ${first.year}`;
                } else {
                  hijriLabel = `${first.monthName} ${first.year} — ${last.monthName} ${last.year}`;
                }
              } else if (first) {
                hijriLabel = `${first.monthName} ${first.year}`;
              } else if (last) {
                hijriLabel = `${last.monthName} ${last.year}`;
              }
              if (!hijriLabel) return null;
              return (
                <div style={{
                  fontSize: '14px',
                  color: theme?.colors?.primary || '#1A5632',
                  fontWeight: '500',
                  marginBottom: '4px',
                  opacity: 0.8,
                }}>
                  ☪ {hijriLabel}
                </div>
              );
            })()}
            <div style={{
              fontSize: '16px',
              color: theme?.colors?.textSecondary || '#64748b',
              fontWeight: '500'
            }}>
              {eventCount > 0
                ? `${eventCount} programs scheduled`
                : occasionMap && occasionMap.size > 0
                  ? 'Islamic occasions this month'
                  : 'No programs scheduled'}
            </div>
          </div>


        </div>

        {/* Calendar Content - Both Views */}
        {events.length === 0 && (!occasionMap || occasionMap.size === 0) ? (
          <div style={styles.calendarContainer}>
            <div style={{ textAlign: 'center', padding: '40px', color: theme?.colors?.cardTextSecondary || '#475569' }}>
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>No events scheduled for {getMonthName(currentMonth)} {currentYear}</p>
              <p style={{ fontSize: '14px' }}>Check back later for updates!</p>
            </div>
          </div>
        ) : (
          <>
            {/* Calendar Section - Wrapped for screenshot capture */}
            <div data-calendar-section="true">
              {/* Screenshot-only Header - Hidden by default, shown only during capture */}
              <div 
                data-screenshot-header="true"
                style={{ 
                  display: 'none', // HIDDEN by default
                  textAlign: 'center', 
                  marginBottom: '16px',
                  backgroundColor: 'white',
                  padding: '12px 8px 8px 8px'
                }}
              >
                <h2 style={{ 
                  fontSize: isMobile ? '28px' : '32px', 
                  fontWeight: '600',
                  margin: '0 0 4px 0',
                  color: theme?.colors?.primary || '#0f172a'
                }}>
                  {getMonthName(currentMonth)} {currentYear}
                </h2>
                <div style={{
                  fontSize: '14px',
                  color: theme?.colors?.cardTextSecondary || '#64748b'
                }}>
                  {centerName}
                </div>
              </div>
              
              {/* Calendar Grid View */}
              <div style={styles.calendarContainer}>
                <CalendarGrid
                  events={events}
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  onDateClick={handleDateClick}
                  onEventSponsor={config?.support_enabled === true ? handleEventSponsor : undefined}
                  theme={theme}
                  occasionMap={occasionMap}
                />
              </div>
            </div>
            
            {/* Calendar Legend - Show after calendar grid */}
            <CalendarLegend 
              theme={theme}
              visibleEventTypes={[...new Set(events.map(e => e.event_type))]}
            />
            
            {/* List View — past months show all events (faded); current/future show only upcoming */}
            <div style={{ ...styles.calendarContainer, marginTop: '24px' }}>
              {(() => {
                const _now = new Date();
                const _todayMonth = _now.getMonth() + 1;
                const _todayYear = _now.getFullYear();
                const isPastMonth = currentYear < _todayYear ||
                  (currentYear === _todayYear && currentMonth < _todayMonth);
                const headerText = isPastMonth
                  ? `Events in ${getMonthName(currentMonth)} ${currentYear}`
                  : `Upcoming Events in ${getMonthName(currentMonth)}`;
                return (
                  <>
                    <h2 style={{
                      fontSize: isMobile ? '20px' : '24px',
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: theme?.colors?.primary || '#0f172a',
                      textAlign: 'center'
                    }}>
                      {headerText}
                    </h2>
                    <CalendarList
                      events={events}
                      onEventSponsor={config?.support_enabled === true ? handleEventSponsor : undefined}
                      theme={theme}
                      showOnlyUpcoming={!isPastMonth}
                      isPastMonth={isPastMonth}
                      currentMonth={currentMonth}
                      currentYear={currentYear}
                      occasionMap={occasionMap}
                    />
                  </>
                );
              })()}
            </div>
          </>
        )}

        {/* Bottom Quote */}
        <div style={styles.quote}>
          Imam Ali Reza (AS) has said: "He who sits in a gathering in which our affairs are discussed, 
          his heart shall not die on the day when hearts shall die."
        </div>

        {/* Day Detail Modal */}
        {showDayModal && selectedDate && (
          <DayDetailModal
            date={selectedDate.date}
            events={selectedDate.events}
            onClose={() => setShowDayModal(false)}
            onEventSponsor={config?.support_enabled === true ? handleEventSponsor : undefined}
            centerName={centerName}
            theme={theme}
            occasionData={occasionMap?.get(selectedDate.date) || null}
          />
        )}

        {/* Sponsorship Flow */}
        {sponsoringEvent && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setSponsoringEvent(null)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <SponsorshipFlow
                announcement={{
                  ...sponsoringEvent,
                  title: sponsoringEvent.isMonthlySupport 
                    ? `Support ${getMonthName(currentMonth)} Programs`
                    : sponsoringEvent.title,
                  event_date: sponsoringEvent.start_date,
                  sponsor_enabled: true
                }}
                centerConfig={config}
                onClose={() => setSponsoringEvent(null)}
                skipInitial={true}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Floating Action Button - Removed view toggle since both views are shown */}
      <FloatingActionButton
        currentView="both"
        onViewToggle={() => {}}
        onSupport={handleMonthlySupport}
        onShare={() => {}}
        monthName={getMonthName(currentMonth)}
        centerName={centerName}
        eventCount={eventCount}
        theme={theme}
      />

      {/* Add spinning animation keyframes */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
};

export default CalendarPage;